import { useEffect, useState } from 'react';
import { Mic, Pause, Play, Square } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { formatDuration } from '@/lib/utils/time';
import { useEncounterSession } from '@/components/audio/EncounterSessionProvider';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';

interface AudioBarProps {
  onRecordingStopped?: (blob: Blob, duration: number) => void;
}

export function AudioBar({ onRecordingStopped }: AudioBarProps) {
  const { startRecording, pauseRecording, resumeRecording, stopRecording } =
    useEncounterSession();
  const { recordingMeta } = useEncounterSessionStore();
  const [elapsed, setElapsed] = useState(0);

  const isRecording = recordingMeta.status === 'recording';
  const isPaused = recordingMeta.status === 'paused';
  const isActive = isRecording || isPaused;

  useEffect(() => {
    if (isRecording && recordingMeta.startedAt) {
      const tick = () => {
        setElapsed(
          Date.now() - recordingMeta.startedAt! - recordingMeta.pausedDuration,
        );
      };
      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }

    if (isPaused && recordingMeta.startedAt) {
      setElapsed(
        Date.now() - recordingMeta.startedAt - recordingMeta.pausedDuration,
      );
    }

    if (!isActive) {
      setElapsed(0);
    }
  }, [
    isRecording,
    isPaused,
    isActive,
    recordingMeta.startedAt,
    recordingMeta.pausedDuration,
  ]);

  return (
    <div
      role="status"
      aria-label={
        isRecording
          ? 'Recording in progress'
          : isPaused
            ? 'Recording paused'
            : 'Not recording'
      }
      className={cn(
        'sticky top-0 z-30 flex items-center gap-3 border-b px-4 py-2',
        isActive
          ? 'border-record/30 bg-record/5'
          : 'border-border bg-muted/40',
      )}
    >
      {isActive ? (
        <>
          {/* Pulsing red dot */}
          <span className="relative flex h-3 w-3 shrink-0">
            {isRecording && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-record opacity-75" />
            )}
            <span
              className={cn(
                'relative inline-flex h-3 w-3 rounded-full',
                isPaused ? 'bg-record/70' : 'bg-record',
              )}
            />
          </span>

          <span className="text-sm font-medium">Recording</span>

          <span className="text-sm font-semibold tabular-nums">
            {formatDuration(elapsed)}
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            <Button
              size="sm"
              type="button"
              variant="ghost"
              className="gap-1.5"
              aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
              onClick={isPaused ? resumeRecording : pauseRecording}
            >
              {isPaused ? (
                <Play className="h-3.5 w-3.5" />
              ) : (
                <Pause className="h-3.5 w-3.5" />
              )}
              <span className="hidden sm:inline">{isPaused ? 'Resume' : 'Pause'}</span>
            </Button>
            <Button
              size="sm"
              type="button"
              variant="ghost"
              className="gap-1.5"
              aria-label="Stop recording"
              onClick={async () => {
                const result = await stopRecording();
                if (result && onRecordingStopped) {
                  onRecordingStopped(result.blob, result.duration);
                }
              }}
            >
              <Square className="h-3.5 w-3.5 fill-record text-record" />
              <span className="hidden sm:inline">Stop</span>
            </Button>
          </div>
        </>
      ) : (
        <>
          <span className="text-sm text-muted-foreground">Not recording</span>

          <div className="ml-auto">
            <Button
              size="sm"
              type="button"
              variant="record"
              className="gap-1.5"
              aria-label="Start recording"
              onClick={startRecording}
            >
              <Mic className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Start Recording</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
